import { TaskModel } from '@bryntum/gantt';
import { isEmpty } from 'lodash';
import moment, { MomentInput } from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import {
  DependenciesMap,
  Project,
  ProjectBaseline,
  ProjectPhase,
} from 'api/project';
import { TaskType } from 'api/tasks';
import { DependenciesData } from 'components/atoms/gantt';
import { GanttBarColors } from 'components/colors';
import { Task } from 'data/tasks';
import {
  DateShiftUpdates,
  EntityType,
  List,
  getDaysDiff,
  getDuration,
} from 'utils/date-shift';
import {
  BaselineList,
  GanttAssignee,
  GanttPhase,
  GanttTask,
  PhaseList,
  TaskList,
  LeastSlackDep,
} from './types';

export enum GanttType {
  ProjectPhaseBar = 'PROJECT_PHASE',
  TaskBar = 'TASK',
  MilestoneBar = 'MILESTONE',
}

export const GANTT_TOOLTIP_DATE_FORMAT = 'MMM DD, YYYY';

export const getGanttPhaseId = (id: number): string =>
  `${GanttType.ProjectPhaseBar}-${id}`;

export const getGanttTaskId = (id: number): string =>
  `${GanttType.TaskBar}-${id}`;

export const formatDate = (date: MomentInput, format?: string): MomentInput =>
  moment(date).format(format ?? DEFAULT_DATE_FORMAT);

/**
 * Bryntum doesn't include the end/ due date for plotting gantt bars
 * hence to make the due date inclusive, it is incremented by 1
 * @param dueDate
 * @returns
 */
export const getDueDateForGantt = (dueDate: MomentInput): MomentInput =>
  formatDate(moment(dueDate).add(1, 'day'));

/**
 * Since due date is incremented by 1 and passed to the gantt for the reason in the API `getDueDateForGantt`
 * to retrieve the actual due date from task record in gantt store
 * it is decremented by 1.
 * @param entity
 * @returns
 */
export const getDueDateFromGantt = (
  entity: TaskModel,
  format?: string,
): MomentInput => formatDate(moment(entity.endDate).subtract(1, 'day'), format);

export const getDependenciesData = (
  phaseList: PhaseList,
  taskList: TaskList,
): DependenciesData[] => {
  const dependenciesData: DependenciesData[] = [];

  Object.values(phaseList).forEach((phase) => {
    phase.dependencies?.forEach((dependencyId) => {
      if (phaseList[dependencyId]) {
        dependenciesData.push({
          id: `${phase.id}-${dependencyId}`,
          fromEvent: `${GanttType.ProjectPhaseBar}-${dependencyId}`,
          toEvent: `${GanttType.ProjectPhaseBar}-${phase.id}`,
        });
      }
    });
  });

  Object.values(taskList).forEach((task) => {
    task.dependencies?.forEach((dependencyId) => {
      if (taskList[dependencyId]) {
        dependenciesData.push({
          id: `${task.id}-${dependencyId}`,
          fromEvent: `${GanttType.TaskBar}-${dependencyId}`,
          toEvent: `${GanttType.TaskBar}-${task.id}`,
        });
      }
    });
  });

  return dependenciesData;
};

export const getPhaseList = (phases: ProjectPhase[]): List => {
  const result: List = {};

  phases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDate,
      dueDate,
      taskOrder,
      tasks,
      dependentProjectPhases,
    }) => {
      result[projectPhaseId] = {
        depth: 0,
        id: projectPhaseId,
        name: projectPhaseName,
        type: EntityType.Phase,
        startDate,
        dueDate,
        children: taskOrder ?? tasks.map((_task) => _task.taskId ?? _task),
        dependentOn: dependentProjectPhases?.map(
          ({ projectPhaseId: dPhaseId }) => dPhaseId,
        ),
      };

      dependentProjectPhases?.forEach(({ projectPhaseId: dPhaseId }) => {
        result[dPhaseId] = {
          ...(result[dPhaseId] ?? {}),
          dependencyFor: [
            ...(result[dPhaseId]?.dependencyFor ?? []),
            projectPhaseId,
          ],
        };
      });
    },
  );

  return result;
};

export const getTaskList = (tasks: Task[], phaseList: List): List => {
  const result: List = {};

  tasks.forEach(
    ({
      taskId,
      startDate,
      dueDate,
      subTasks,
      dependentTasks,
      parentTask,
      projectPhase,
      taskName,
    }) => {
      result[taskId] = {
        ...(result[taskId] ?? {}),
        depth: 1, // initial value
        name: taskName,
        id: taskId,
        type: EntityType.Task,
        hasStartDate: !!startDate,
        hasDueDate: !!dueDate,
        startDate,
        dueDate,
        children: subTasks?.map((_task) => _task),
        dependentOn: dependentTasks?.map(({ taskId: dTaskId }) => dTaskId),
        parent: parentTask ?? projectPhase?.projectPhaseId,
        projectPhaseId: projectPhase?.projectPhaseId,
      };

      dependentTasks?.forEach(({ taskId: dTaskId }) => {
        result[dTaskId] = {
          ...(result[dTaskId] ?? {}),
          dependencyFor: [...(result[dTaskId]?.dependencyFor ?? []), taskId],
        };
      });
    },
  );

  const getDepth = (id) => {
    return !result[id]?.parent ? 0 : getDepth(result[id].parent) + 1;
  };

  const getStartDate = (id: number): MomentInput => {
    const startDate = (result[id] || phaseList[id])?.startDate;
    const parent = (result[id] || phaseList[id])?.parent;

    return startDate || (parent && getStartDate(parent));
  };

  Object.values(result).forEach((_task) => {
    result[_task.id] = {
      ..._task,
      depth: getDepth(_task.id),
    };
  });

  /**
   * Pick parent's start date for empty start date.
   */
  Object.values(result)
    .filter((_task) => !_task.startDate)
    .forEach((_task) => {
      result[_task.id] = {
        ..._task,
        startDate: _task.dueDate || getStartDate(_task.id),
      };
    });

  /**
   * Assign due date same as start date for empty due date.
   */
  Object.values(result)
    .filter((_task) => !_task.dueDate)
    .forEach((_task) => {
      result[_task.id] = {
        ..._task,
        dueDate: _task.startDate,
      };
    });

  return result;
};

export const getGanttPhaseList = (
  rawPhases: ProjectPhase[],
  baselineList?: BaselineList['phases'],
) => {
  const tPhases = {} as PhaseList;

  rawPhases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDate,
      dueDate,
      project,
      fields,
      dependentProjectPhases,
      ...rest
    }) => {
      tPhases[projectPhaseId] = {
        id: projectPhaseId,
        name: projectPhaseName,
        projectId: project.projectId,
        startDate,
        dueDate,
        fields,
        type: 'phase',
        private: rest.private,
        children: rest.taskOrder ?? [],
        baselines: baselineList?.[projectPhaseId],
        dependencies:
          dependentProjectPhases?.map(
            ({ projectPhaseId: _phaseId }) => _phaseId,
          ) ?? [],
        // status: _phase.progressStatus,
      } as unknown as GanttPhase;
    },
  );

  return tPhases;
};

export const filterTaskUpdates = (
  taskUpdates: DateShiftUpdates,
  _tasks: Task[],
): DateShiftUpdates => {
  const filteredUpdates: DateShiftUpdates = {};

  Object.values(taskUpdates).forEach((update) => {
    const { id } = update;
    const _update = { ...update };
    const task = _tasks.find((_task) => _task.taskId === id);

    if (!task?.startDate && _update.startDate) {
      delete _update.startDate;
    }

    if (!task?.dueDate && _update.dueDate) {
      delete _update.dueDate;
    }

    filteredUpdates[id] = { ..._update };
  });

  return filteredUpdates;
};

export const getParentStartDate = (id: number, lookup: List): MomentInput => {
  const { startDate, parent } = lookup[id];

  return parent && lookup[parent].startDate
    ? lookup[parent].startDate
    : parent && getParentStartDate(parent, lookup)
    ? getParentStartDate(parent, lookup)
    : startDate;
};

export const convertToGanttTask = ({
  taskId,
  taskName,
  startDate,
  dueDate,
  projectPhase,
  parentTask,
  subTasks,
  project,
  dependentTasks,
  completedAt,
  responsible,
  progress,
  assignee,
  type,
  fields,
  status,
  formInstanceId,
  ...rest
}: Task): GanttTask => {
  return {
    id: taskId,
    name: taskName,
    projectId: project,
    startDate,
    dueDate,
    type: type ?? TaskType.Task,
    parent: parentTask ?? projectPhase?.projectPhaseId,
    children: subTasks ?? [],
    private: rest.private,
    completedAt,
    responsible,
    progress,
    fields,
    dependencies: dependentTasks?.map(({ taskId: _taskId }) => _taskId) ?? [],
    assignees:
      assignee?.users?.map(
        ({ userId, firstName, lastName }) =>
          ({
            id: userId,
            firstName,
            lastName,
          } as GanttAssignee),
      ) ?? [],
    status,
    formInstanceId,
  } as GanttTask;
};

export const getGanttTaskList = (
  rawTasks: Task[],
  baselineList: BaselineList['tasks'],
): TaskList => {
  const tTasks = {} as TaskList;

  rawTasks.forEach((task) => {
    tTasks[task.taskId] = {
      ...convertToGanttTask(task),
      baselines: baselineList?.[task.taskId],
    };
  });

  return tTasks;
};

export const getGanttTaskListWithoutBaselines = (
  rawTasks: Task[],
): TaskList => {
  const tTasks = {} as TaskList;

  rawTasks.forEach((task) => {
    tTasks[task.taskId] = {
      ...convertToGanttTask(task),
    };
  });

  return tTasks;
};

export const convertToGanttStoreFormat = (
  item: GanttTask,
  parentStartDate: MomentInput,
  rootIndex: number,
  currentLevel: number,
) => {
  let startDate: MomentInput;
  let endDate: MomentInput;

  if (!item.dueDate) {
    startDate = formatDate(item.startDate || parentStartDate);
    endDate = getDueDateForGantt(startDate);
  } else if (!item.startDate) {
    endDate = getDueDateForGantt(item.dueDate);
    startDate = formatDate(item.dueDate);
  } else {
    startDate = formatDate(item.startDate);
    // endDate = moment(item.dueDate).format(DEFAULT_DATE_FORMAT);
    endDate = getDueDateForGantt(item.dueDate);
  }
  /**
   * Gantt excludes end date by default; hence to include end date, we are
   * 1. marking due date as end of day of the set due date explicitly
   * 2. avoid passing duration
   */
  // const duration = moment(endDate).diff(moment(startDate), 'days');

  return {
    id: `${GanttType.TaskBar}-${item.id}`,
    expanded: true,
    manuallyScheduled: true,
    title: item.name,
    name: item.name,
    cls: `gantt-task ${item.private ? 'gantt-private-row' : ''} gantt-bg-${
      GanttBarColors[rootIndex % GanttBarColors.length]
    } gantt-level-${currentLevel}`,
    startDate,
    endDate,
    // duration: item.type === `${GanttType.MilestoneBar}` ? 0 : duration || 1,

    type: GanttType.TaskBar,
    data: { ...item, rootIndex, currentLevel },
    percentDone: item.progress,
    isCalculatedStartDate: !item.startDate,
    isCalculatedEndDate: !item.dueDate,
    baselines: item.baselines,
    totalSlack: item.slack,
    critical: item.slack && item.slack <= 0,
  };
};

const mapTasks = (
  taskIds: number[],
  taskList: TaskList,
  parentStartDate: MomentInput,
  rootIndex: number,
  parentLevel: number,
) => {
  const currentLevel = parentLevel + 1;

  return taskIds
    .map((itemId) => {
      const item = taskList[itemId];

      if (!item) {
        return null;
      }
      const formattedTask = convertToGanttStoreFormat(
        item,
        parentStartDate,
        rootIndex,
        currentLevel,
      );

      return {
        ...formattedTask,
        children: mapTasks(
          item.children,
          taskList,
          formattedTask.startDate,
          rootIndex,
          currentLevel,
        ),
      };
    })
    .filter(Boolean);
};

export const mapPhases = (
  phaseOrder: number[],
  phases: PhaseList,
  tasks: TaskList,
) => {
  const orderedPhases = phaseOrder.map((phaseId) => phases[phaseId]);

  return Object.entries(orderedPhases).map(([, phase], index) => {
    const startDate = formatDate(phase.startDate);

    // const endDate = moment(phase.dueDate).format(DEFAULT_DATE_FORMAT);
    const endDate = getDueDateForGantt(phase.dueDate);

    return {
      id: getGanttPhaseId(phase.id),
      expanded: true,
      manuallyScheduled: true,
      resizable: false,
      title: phase.name,
      name: phase.name,
      cls: `gantt-project_phase ${
        phase.private ? 'gantt-private-row' : ''
      } gantt-bg-${
        GanttBarColors[index % GanttBarColors.length]
      }  level-${index}`,
      startDate,
      endDate,
      // duration: moment(phase.dueDate).diff(moment(phase.startDate), 'days'),
      children: phase.children
        ? mapTasks(phase.children, tasks, startDate, index, 0)
        : [],
      type: GanttType.ProjectPhaseBar,
      data: phase,
      baselines: phase.baselines,
      totalSlack: phase.slack,
      critical: phase.slack && phase.slack <= 0,
    };
  });
};

export const getBaselineList = (
  baselines: ProjectBaseline[],
  phaseIds: number[],
  taskIds: number[],
): BaselineList => {
  const list = {} as BaselineList;

  list.phases = {};
  list.tasks = {};

  baselines?.forEach(({ projectPhases, tasks, baselineName, baselineId }) => {
    const phaseMap = projectPhases.reduce(
      (prev, _phase) => ({
        ...prev,
        [_phase.projectPhaseId]: _phase,
      }),
      {},
    );

    const taskMap = tasks.reduce(
      (prev, _task) => ({
        ...prev,
        [_task.taskId]: _task,
      }),
      {},
    );

    phaseIds?.forEach((_id) => {
      const baseline = phaseMap[_id];

      list.phases[_id] = [
        ...(list.phases[_id] || []),
        {
          startDate: baseline
            ? moment(baseline.startDate).format('YYYY-MM-DD')
            : null,
          endDate: baseline
            ? moment(getDueDateForGantt(baseline.dueDate)).format('YYYY-MM-DD')
            : null,
          name: baselineName,
          id: baselineId,
        },
      ];
    });
    taskIds?.forEach((_id) => {
      const baseline = taskMap[_id];

      list.tasks[_id] = [
        ...(list.tasks[_id] || []),
        {
          startDate: baseline
            ? moment(baseline.startDate).format('YYYY-MM-DD')
            : null,
          endDate: baseline
            ? moment(getDueDateForGantt(baseline.dueDate)).format('YYYY-MM-DD')
            : null,
          name: baselineName,
          id: baselineId,
        },
      ];
    });
  });

  return list;
};

export const computeSlack = (
  phaseList: PhaseList,
  taskList: TaskList,
  projectDetail: Project,
): [PhaseList, TaskList] => {
  const { dueDate: projDueDate } = projectDetail;
  const projectDueDate = moment(projDueDate);
  const phaseDepMap: { [id: number]: number[] } = {};
  const taskDepMap: { [id: number]: number[] } = {};

  const phaseLeastSlackDepMap: { [id: number]: LeastSlackDep } = {};
  const taskLeastSlackDepMap: { [id: number]: LeastSlackDep } = {};

  const phaseDurationMap: { [id: number]: number } = {};
  const taskDurationMap: { [id: number]: number } = {};

  const getDateForEmptyCase = (id: number): MomentInput => {
    const startDate = (taskList[id] || phaseList[id])?.startDate;
    const parent = (taskList[id] || phaseList[id])?.parent;

    return startDate || (parent && getDateForEmptyCase(parent));
  };

  /** duration calculation */
  Object.values(phaseList).forEach(({ id, startDate, dueDate }) => {
    phaseDurationMap[id] =
      startDate && dueDate && startDate !== dueDate
        ? getDuration(startDate, dueDate)
        : 1;
  });

  Object.values(taskList).forEach(({ id, startDate, dueDate }) => {
    taskDurationMap[id] =
      startDate && dueDate && startDate !== dueDate
        ? getDuration(startDate, dueDate)
        : 1;
  });

  /** dependency map generation */
  Object.values(phaseList).forEach(({ id, dependencies }) => {
    dependencies.forEach((_depId) => {
      phaseDepMap[_depId] = [...(phaseDepMap[_depId] ?? []), id];
    });
  });

  Object.values(taskList).forEach(({ id, dependencies }) => {
    dependencies.forEach((_depId) => {
      taskDepMap[_depId] = [...(taskDepMap[_depId] ?? []), id];
    });
  });

  /**
   * this computes the minimum possible duration
   * based on the children duration and longest dependency chain.
   */
  const getMinDuration = (id: number, entity?: string): number => {
    const { children } = entity === 'phase' ? phaseList[id] : taskList[id];
    const childMaxDuration = children.reduce(
      (highestDuration, _id) =>
        taskDurationMap[_id] > highestDuration
          ? taskDurationMap[_id]
          : highestDuration,
      0,
    );

    const depChain: number[][] = [];
    const sortedChildrenWithDeps = children
      .filter((_id) => taskDepMap[_id]?.length > 0)
      .sort((_id1, _id2) => {
        const startDate1 =
          taskList[_id1].startDate ?? getDateForEmptyCase(_id1);
        const startDate2 =
          taskList[_id2].startDate ?? getDateForEmptyCase(_id2);

        return moment(startDate1).diff(moment(startDate2), 'd');
      });

    const visitedNodes = new Set();

    const depIterator = (paths: number[][]) => {
      const iPaths: number[][] = [];

      paths.forEach((_path) => {
        const _id = _path[_path.length - 1];

        visitedNodes.add(_id);

        const _deps = taskDepMap[_id];

        if (!_deps) {
          depChain.push(_path);
        } else {
          _deps.forEach((_depId) => {
            iPaths.push([..._path, _depId]);
          });
        }
      });

      if (iPaths.length > 0) {
        depIterator(iPaths);
      }
    };

    sortedChildrenWithDeps.forEach((_id) => {
      if (!visitedNodes.has(_id)) {
        depIterator([[_id]]);
      }
    });

    let depChainMaxDuration = 0;

    depChain.forEach((_chain) => {
      let _duration = 0;

      _chain.forEach((_id) => {
        _duration += taskDurationMap[_id];
      });
      if (_duration > depChainMaxDuration) {
        depChainMaxDuration = _duration;
      }
    });

    return Math.max(childMaxDuration, depChainMaxDuration);
  };

  /** dependency slack calculation */
  const getPhaseDepSlack = (id: number): number => {
    const { dueDate } = phaseList[id];
    const dependencies = phaseDepMap[id];

    if (!dependencies) {
      return getDaysDiff(projectDueDate, moment(dueDate));
    }

    const computed = phaseLeastSlackDepMap[id];

    if (computed) {
      return computed.slack;
    }

    let targetDepId = dependencies[0];
    let targetDepSlack;

    /** least slack dependency computation */
    dependencies.forEach((_depId) => {
      const depNode = phaseList[_depId];
      let additionalAvlSlack = 0;

      if (depNode.children.length > 0) {
        const currentDuration = getDuration(depNode.startDate, depNode.dueDate);
        const minDuration = getMinDuration(_depId, 'phase');

        additionalAvlSlack = Math.max(currentDuration - minDuration, 0);
      }
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const _slack = getPhaseSlack(_depId);
      const gap = getDaysDiff(moment(depNode.startDate), moment(dueDate)) - 1;

      const finalSlack = gap + (_slack ?? 0) + additionalAvlSlack;

      if (targetDepId === _depId || finalSlack < targetDepSlack) {
        targetDepSlack = finalSlack;
        targetDepId = _depId;
      }
    });
    phaseLeastSlackDepMap[id] = { depId: targetDepId, slack: targetDepSlack };

    return targetDepSlack;
  };

  const getTaskDepSlack = (id: number): number => {
    const { dueDate } = taskList[id];
    const dependencies = taskDepMap[id];

    if (!dependencies) {
      return getDaysDiff(projectDueDate, moment(dueDate));
    }

    const computed = taskLeastSlackDepMap[id];

    if (computed) {
      return computed.slack;
    }

    let targetDepId = dependencies[0];
    let targetDepSlack;

    /** least slack dependency computation */
    dependencies.forEach((_depId) => {
      const depNode = taskList[_depId];
      let additionalAvlSlack = 0;

      if (depNode.children.length > 0) {
        const currentDuration = getDuration(depNode.startDate, depNode.dueDate);
        const minDuration = getMinDuration(_depId, 'task');

        additionalAvlSlack = Math.max(currentDuration - minDuration, 0);
      }

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const _slack = getTaskSlack(_depId);
      const gap = getDaysDiff(moment(depNode.startDate), moment(dueDate)) - 1;

      const finalSlack = gap + (_slack ?? 0) + additionalAvlSlack;

      if (targetDepId === _depId || finalSlack < targetDepSlack) {
        targetDepSlack = finalSlack;
        targetDepId = _depId;
      }
    });
    taskLeastSlackDepMap[id] = { depId: targetDepId, slack: targetDepSlack };

    return targetDepSlack;
  };

  const getPhaseSlack = (id: number): number => {
    const { slack } = phaseList[id];

    if (slack || slack === 0) {
      return slack;
    }

    return getPhaseDepSlack(id);
  };

  const getTaskSlack = (id: number): number => {
    const { slack } = taskList[id];

    if (slack || slack === 0) {
      return slack;
    }

    return getTaskDepSlack(id);
  };

  const setSlackForDownTree = (id: number, entityType?: string) => {
    const {
      dueDate: parentDueDate,
      children,
      slack,
    } = entityType === 'phase' ? phaseList[id] : taskList[id];

    children.forEach((_child) => {
      if (!taskList[_child]) {
        return;
      }
      const { dueDate: _dueDate } = taskList[_child];
      const dueDate = _dueDate ?? getDateForEmptyCase(id);
      const gap = getDaysDiff(moment(parentDueDate), moment(dueDate));

      taskList[_child].slack = slack + gap;

      setSlackForDownTree(_child);
    });
  };

  const validateAndUpdateSlackInUpTree = (id: number) => {
    const _task = taskList[id];

    if (!_task || !_task.parent) {
      return;
    }

    const { startDate: childStartDate, parent, slack: childSlack } = _task;

    const { startDate, slack } = taskList[parent] ?? phaseList[parent];
    const gap = getDaysDiff(moment(childStartDate), moment(startDate));
    const newSlack = childSlack + gap;

    if (slack && newSlack < slack) {
      (taskList[parent] ?? phaseList[parent]).slack = newSlack;
      validateAndUpdateSlackInUpTree(parent);
    }
  };

  Object.values(phaseList).forEach(({ id, dueDate }) => {
    const dependencies = phaseDepMap[id];

    if (!dependencies) {
      phaseList[id].slack = getDaysDiff(projectDueDate, moment(dueDate));
    } else {
      const depSlack = getPhaseDepSlack(id);

      phaseList[id].slack = depSlack;
      setSlackForDownTree(id, 'phase');
    }
  });

  Object.values(taskList).forEach(({ id, dueDate: _dueDate, slack }) => {
    const dueDate = _dueDate ?? getDateForEmptyCase(id);

    const dependencies = taskDepMap[id];
    const newSlack = !dependencies
      ? getDaysDiff(projectDueDate, moment(dueDate))
      : getTaskDepSlack(id);
    const setNewSlack = (!slack && slack !== 0) || newSlack < slack;

    if (setNewSlack) {
      taskList[id].slack = newSlack;

      if (dependencies) {
        setSlackForDownTree(id);
        validateAndUpdateSlackInUpTree(id);
      }
    }
  });

  return [phaseList, taskList];
};

export const getExtremeDates = (
  dateSpanArray: [Date, Date],
  currentDates: [Date, Date],
): [Date, Date] => {
  const newDates: [Date, Date] = [...dateSpanArray];

  if (!isEmpty(currentDates)) {
    newDates[0] =
      newDates[0] && moment(newDates[0]).isBefore(moment(currentDates[0]))
        ? newDates[0]
        : currentDates[0];
    newDates[1] =
      newDates[1] && moment(newDates[1]).isAfter(moment(currentDates[1]))
        ? newDates[1]
        : currentDates[1];
  }

  return newDates;
};

export const isValidDrop = (
  source,
  target,
  dependenciesMap: DependenciesMap,
) => {
  const { id: sourceId } = source.get('data');
  const { id: targetId } = target.get('data');

  const { _id: sourceTemplateId } = source.originalData;
  const { _id: targetTemplateId } = source.originalData;

  const _source = sourceId ?? sourceTemplateId;
  const _target = targetId ?? targetTemplateId;

  const map =
    source.type === GanttType.ProjectPhaseBar
      ? dependenciesMap.phaseMap
      : dependenciesMap.taskMap;

  if (
    !map[_source] &&
    dependenciesMap.taskFamily &&
    !dependenciesMap.taskFamily[_source]
  ) {
    return true;
  }

  const isTaskIdPresent = map[_source]?.some((id) => id === _target) ?? false;

  if (isTaskIdPresent) {
    return false;
  }

  if (
    !isTaskIdPresent &&
    dependenciesMap.taskFamily[_source] &&
    dependenciesMap.taskFamily[_source].some((id) => id === _target)
  ) {
    return false;
  }

  return true;
};
